import React, { useState } from "react";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import img from "../../../Images/LoginImages/Mask group.png";
import { login } from "../../../Redux/authSlice";
import { saveToken, saveUser } from "../../Avatar/Utility";
import useApiFetcher from "../../../hooks/useApiFetcher";
import BouncingDotsLoader from "../../Loaders/BouncingDotsLoader";

const Login = () => {
  const { apiFetcher } = useApiFetcher();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleValueChange = (e) => {
    setEmail(e.target.value.replace(/\s/g, ''));
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value.replace(/\s/g, ''));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await apiFetcher("/auth/login", "POST", { email, password });
      const token = response?.token;
      const user = response?.user;
      if (token && user) {
        dispatch(login({ token, user }));
        saveUser(user);
        saveToken(token);
      } else {
        console.error("Login failed: Token not received");
      }
    } catch (error) {
      console.error("Login failed:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgot = (e) => {
    e.preventDefault();
    navigate("/forgotPassword");
  };

  const handleKeyDown = (event) => {
    if (event.key === ' ') {
      event.preventDefault(); 
    }
  };

  return (
    <div className="min-h-screen bg-blue-50 flex items-center justify-center p-4">
      
      {/* Background ambient light */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 bg-primary-600/10 rounded-full blur-[120px]"></div>
        <div className="absolute -bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-accent-600/10 rounded-full blur-[120px]"></div>
      </div>

      <div className="relative w-full max-w-5xl h-[600px] flex rounded-3xl overflow-hidden glass-panel shadow-2xl z-10 border border-blue-200/50">
        
        {/* Left Section - Image */}
        <div className="hidden md:flex w-1/2 bg-white relative items-center justify-center overflow-hidden border-r border-blue-200/50">
          <div className="absolute inset-0 bg-gradient-to-t from-dark-900 to-transparent z-10 opacity-60"></div>
          <img src={img} alt="Car Service" className="absolute inset-0 w-full h-full object-cover" />
          <div className="relative z-20 text-center p-8 mt-auto mb-10 text-white drop-shadow-lg">
             <h3 className="text-3xl font-bold mb-2">Welcome Back</h3>
             <p className="text-slate-700">Log in to manage your AI avatars and streams.</p>
          </div>
        </div>

        {/* Right Section - Form */}
        <div className="w-full md:w-1/2 flex flex-col justify-center p-8 md:p-12 bg-blue-50/60 backdrop-blur-xl">
          <div className="max-w-md w-full mx-auto">
            <h2 className="text-3xl font-bold text-white mb-2 text-center md:text-left">Sign In</h2>
            <p className="text-slate-600 mb-8 text-center md:text-left">Access your SaleSense.ai dashboard</p>
            
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-slate-700 ml-1">Email</label>
                <input
                  type="text"
                  placeholder="Enter your email"
                  value={email}
                  className="input-field"
                  onKeyDown={handleKeyDown}
                  onChange={handleValueChange}
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-slate-700 ml-1">Password</label>
                <div className="relative flex items-center">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={handlePasswordChange}
                    className="input-field pr-12"
                    onKeyDown={handleKeyDown}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-4 text-slate-600 hover:text-primary-400 transition-colors focus:outline-none"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? <AiFillEyeInvisible size={20} /> : <AiFillEye size={20} />}
                  </button>
                </div>
                
                <div className="flex justify-end mt-1">
                  <button type="button" onClick={handleForgot} className="text-sm text-primary-500 hover:text-primary-400 hover:underline transition-colors focus:outline-none">
                    Forgot Password?
                  </button>
                </div>
              </div>

              <button type="submit" className="btn-primary mt-4 py-3 flex justify-center items-center h-12" disabled={isLoading}>
                {isLoading ? <BouncingDotsLoader /> : <span className="font-semibold text-lg">Log In</span>}
              </button>
              
            </form>

            <div className="mt-8 pt-8 border-t border-blue-200/50">
               <p className="text-xs text-center text-slate-500 leading-relaxed">
                  By continuing, you agree to our{" "}
                  <a href="/terms" className="text-slate-700 hover:text-primary-400 transition-colors underline">Terms of Service</a>,{" "}
                  <a href="/privacy" className="text-slate-700 hover:text-primary-400 transition-colors underline">Privacy Policy</a>, and{" "}
                  <a href="/content" className="text-slate-700 hover:text-primary-400 transition-colors underline">Content Policy</a>.
               </p>
            </div>
            
          </div>
        </div>

      </div>
    </div>
  );
};

export default Login;
